import { useLocation, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthForm } from '../hooks/useAuthForm';
import { useAuthViewState } from '../hooks/useAuthViewState';
import { useHashError } from '../hooks/useHashError';
import Logger from '../utils/logger';
import LoginNavbar from '../components/auth/LoginNavbar';
import AuthHeader from '../components/auth/AuthHeader';
import AuthForm from '../components/auth/AuthForm';
import AuthNavigation from '../components/auth/AuthNavigation';

export default function Login() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = location.state?.from?.pathname || '/';
  const { isAuthenticated, isLoading } = useAuth();

  const action = searchParams.get('action');
  const mustRecoverPassword = action === 'update_password';

  const hashMessage = useHashError();
  const { viewState, setView, setMessage } = useAuthViewState(mustRecoverPassword, hashMessage);
  const { email, setEmail, password, setPassword, loading, submitForm, handleGoogleSignIn } =
    useAuthForm();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-theme-light-page-bg dark:bg-theme-dark-page-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E34290]"></div>
      </div>
    );
  }

  if (isAuthenticated && !mustRecoverPassword) {
    Logger.info('User already authenticated, redirecting', { from });
    return <Navigate to={from} replace />;
  }

  const handleFormSubmit = async (view: typeof viewState.view) => {
    const result = await submitForm(view);
    if (result) {
      setMessage(result);
      if (view === 'sign_up' && result.type === 'success') {
        localStorage.setItem('justSignedUp', 'true');
      }
    }
  };

  const handleGoogleSignInClick = async () => {
    try {
      await handleGoogleSignIn();
    } catch (error) {
      setMessage({
        text: error instanceof Error ? error.message : 'An error occurred',
        type: 'warn',
      });
    }
  };

  const { view, message } = viewState;

  return (
    <>
      <LoginNavbar />
      <div className="min-h-screen bg-theme-light-page-bg dark:bg-theme-dark-page-bg">
        {/* Main container matching app layout structure */}
        <div className="min-h-screen flex items-center justify-center p-4 lg:p-8">
          <div className="w-full max-w-md">
            {/* Main card with matching design system */}
            <div className="bg-theme-light-bg dark:bg-theme-dark-bg rounded-xl border border-gray-300 dark:border-gray-600 overflow-hidden">
              {/* Header section */}
              <AuthHeader view={view} />

              {/* Form section */}
              <AuthForm
                view={view}
                email={email}
                password={password}
                loading={loading}
                message={message || null}
                onEmailChange={setEmail}
                onPasswordChange={setPassword}
                onSubmit={handleFormSubmit}
                onGoogleSignIn={handleGoogleSignInClick}
                onViewChange={setView}
              />

              {/* Navigation Links */}
              <div className="px-6 pb-6">
                <AuthNavigation view={view} onViewChange={setView} />
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 text-center">
              <p className="text-xs text-theme-light-text-muted dark:text-theme-dark-text-muted">
                By continuing, you agree to our{' '}
                <a
                  href="https://juaai.notion.site/Jua-Legal-0ef8d8d4227c4dda95c431e15a6b9d57"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#E34290] hover:text-[#E34290]/80 transition-colors"
                >
                  Terms of Service
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
